import { CompanyFeature } from '../types/CompanyFeature'
import { FeatureType } from '../types/Feature'
import { MenuItem } from '../types/MenuItem'
import { FeatureFlagManager } from './featureflags'

type MenuDefinition = Partial<Record<FeatureType, MenuItem>>

const Seller: MenuDefinition = {
	'seller-copilot': {
		label: 'Copiloto do Vendedor',
		isSection: true,
		children: [
			{
				label: 'Pesquisa por Loja',
				to: '/copiloto-do-vendedor/pesquisa',
			},
			{
				label: 'Venda AI',
				to: '/copiloto-do-vendedor/venda-ai',
			},
		],
	},
}

const ServiceLevel: MenuDefinition = {
	'service-level': {
		label: 'Nível de Serviço · Sellers',
		to: '/service-level/dashboard' as MenuItem['to'],
	},
}

const Campaigns: MenuDefinition = {
	'campaign-simulator': {
		label: 'Simulador de Campanha',
		to: '/sales/simulador-campanha',
	},
}

const AlertMonitor: MenuDefinition = {
	'alert-monitor': {
		label: 'Monitor de Alertas',
		children: [
			{
				label: 'Lista de alertas',
				to: '/alerts/list',
			},
			{
				label: 'Gerar novo alerta',
				to: '/alerts/new',
			},
		],
	},
}

const Lojistas: MenuDefinition = {
	'lojistas-visao-geral': {
		label: 'Visão Geral',
		to: '/lojistas/geral',
	},
	'lojistas-tabela-reposicao': {
		label: 'Tabela de Reposição',
		to: '/lojistas/tabela-reposicao',
	},
	'lojistas-pedido-compra': {
		label: 'Pedido de Compra',
		to: '/lojistas/pedido-compra',
	},
	'lojistas-importar-dados': {
		label: 'Importar Dados',
		to: '/lojistas/importar',
	},
	'lojistas-fornecedores': {
		label: 'Fornecedores',
		to: '/lojistas/fornecedores',
	},
	'lojistas-configuracoes': {
		label: 'Configurações',
		to: '/lojistas/configuracoes',
	}
}

const Reports: MenuDefinition = {
	'pedidos-report': {
		label: 'Pedidos',
		to: '/reports/pedidos',
	},
	'promocoes-report': {
		label: 'Promoções',
		to: '/reports/promos',
	},
	'notafiscal-report': {
		label: 'Nota Fiscal',
		to: '/reports/notafiscal',
	},
	'cadastrolojistas-report': {
		label: 'Cadastro Lojistas',
		to: '/reports/cadastro_lojistas',
	},
	'vendedordistribuidor-report': {
		label: 'Cadastro Vendedores',
		to: '/reports/cadastro_vendedores',
	},
	'apuracao-report': {
		label: 'Apuração',
		to: '/reports/apuracao',
	},
	'sellout-report': {
		label: 'Sellout',
		to: '/reports/sellout',
	},
	'historico-preco-estoque-report': {
		label: 'Histórico Preço Estoque',
		to: '/reports/historico_preco_estoque',
	},
	'distribuidor-prioritario-report': {
		label: 'Distribuidor Prioritário',
		to: '/reports/distribuidor_prioritario',
	},
	'ultimo-preco-estoque-report': {
		label: 'Último Preço Estoque',
		to: '/reports/ultimo_preco_estoque',
	},
	'lojistas-report': {
		label: 'Cashback Lojistas',
		to: '/reports/lojistas',
	},
	'cupons-report': {
		label: 'Cashback Cupons',
		to: '/reports/cupons',
	},
	'politica-preco-report': {
		label: 'Política de Preço',
		to: '/reports/politica_preco',
	},
	'lojas-venda-premiada-report': {
		label: 'Lojas Venda Premiada',
		to: '/reports/lojas_venda_premiada',
	},
}

const getItems = (features: CompanyFeature[] | '*', menuData: MenuDefinition): MenuItem[] => {
	if (features === '*') return Object.values(menuData).filter(Boolean)
	const featureSet:any = new Set(features.map((f) => f.name))

	return Object.entries(menuData).reduce<MenuItem[]>((acc, [featureName, item]) => {
		if (!item) return acc
		if (!featureSet.has(featureName)) return acc
		if (item.children?.length) {
			const filteredChildren = item.children.filter((child) => !!child.to)
			if (!filteredChildren.length) {
				return acc
			}
			acc.push({
				...item,
				children: filteredChildren,
			})
		} else {
			acc.push(item)
		}
		return acc
	}, [])
}

export const getCampaignsMenu = (companyFeatures: CompanyFeature[]): MenuItem[] => {
	const items = getItems(companyFeatures, Campaigns).sort((a, b) =>
		a.label.localeCompare(b.label),
	)
	if (!items.length) return []
	return [
		{
			label: 'Produtos de dados',
			isSection: true,
			hasBullet: false,
			children: items,
		},
	]
}

export const getLojistasMenu = (features:any): MenuItem[] => {
	const items = getItems(features, Lojistas)
	if (!items.length) return []
	return [
		{
			label: 'Lojistas',
			isSection: true,
			children: items,
		},
	]
}

// Gramática única do sidebar: seção (cabeçalho, só na raiz) → item/acordeão →
// folha. Nenhum nível usa bolinha, só o nome.
export const normalizeMenu = (items: MenuItem[]): MenuItem[] =>
	items.map((item) => ({
		...item,
		hasBullet: false,
		children: item.children?.length ? normalizeMenu(item.children) : item.children,
	}))

export const getToolsMenu = (companyFeatures: CompanyFeature[], isSuperAdmin: boolean): MenuItem[] => {
	const featuresOrAll = isSuperAdmin ? '*' : companyFeatures

	const alertMonitor = getItems(featuresOrAll, AlertMonitor)
	const reports = getItems(featuresOrAll, Reports).sort((a, b) =>
		a.label.localeCompare(b.label)
	)
	const copilot = getItems(featuresOrAll, Seller)

	const serviceLevel = getItems(featuresOrAll, ServiceLevel)

	const tools: MenuItem[] = [
		...alertMonitor,
		...serviceLevel,
		{
			label: 'Relatórios',
			children: reports,
		},
	]
		.filter((menuItem) => {
			if (menuItem.children && !menuItem.children.length) {
				return false
			}
			return true
		})
		.sort((a, b) => a.label.localeCompare(b.label))

	const menu: MenuItem[] = []
	if (tools.length) {
		menu.push({
			label: 'Ferramentas',
			isSection: true,
			children: tools,
		})
	}
	menu.push(...copilot)
	if (isSuperAdmin) {
		menu.push({
			label: 'SUPER ADMIN',
			isSection: true,
			children: [
				{
					label: 'Sistema de Logs',
					to: '/logs',
				},
			],
		})
	}
	return menu
}
